import { useSupabaseClientStore } from "../../stores/supaBaseClientStore";
import { useAuthStore } from "../../stores/authStore";
import { useLogger } from '../../composables/useLogger'

const logger = useLogger()

export enum ConfigurationType {
  Object = 'object',
  Boolean = 'boolean',
  String = 'string',
  Number = 'number',
  Array = 'array',
}

interface ConfigurationStructure {
  type: ConfigurationType;
  default?: any;
  children?: Record<string, ConfigurationStructure>;
}

class Configuration {
  private data: Record<string, any> = {};

  constructor(
    private structure: Record<string, ConfigurationStructure>,
    confData: Record<string, any> = {}
  ) {
    this.initializeData(this.structure, confData, this.data);
  }

  /**
   * Initializes the configuration data based on the provided structure and confData.
   * @param structure The configuration structure defining types and defaults.
   * @param confData The actual configuration data provided by the user.
   * @param currentData The current level in the data hierarchy being populated.
   */
  private initializeData(
    structure: Record<string, ConfigurationStructure>,
    confData: Record<string, any>,
    currentData: Record<string, any>
  ) {
    // Initialize structured data
    for (const [key, value] of Object.entries(structure)) {
      if (value.type === ConfigurationType.Object && value.children) {
        currentData[key] = {};
        this.initializeData(value.children, confData[key] || {}, currentData[key]);
      } else if (value.type === ConfigurationType.Array) {
        currentData[key] = Array.isArray(confData[key]) ? confData[key] : value.default || [];
      } else {
        currentData[key] = this.validateValue(confData[key], value) ?? value.default;
      }
    }

    // Keep additional keys from confData
    for (const [key, value] of Object.entries(confData)) {
      if (!(key in structure)) {
        currentData[key] = value;
      }
    }
  }

  private validateValue(value: any, structure: ConfigurationStructure): any {
    if (value === undefined) return undefined;

    switch (structure.type) {
      case ConfigurationType.Boolean:
        return typeof value === 'boolean' ? value : undefined;
      case ConfigurationType.Number:
        return typeof value === 'number' ? value : undefined;
      case ConfigurationType.String:
        return typeof value === 'string' ? value : undefined;
      case ConfigurationType.Array:
        return Array.isArray(value) ? value : undefined;
      case ConfigurationType.Object:
        return typeof value === 'object' && value !== null ? value : undefined;
      default:
        return undefined;
    }
  }

  /**
   * Retrieves a nested value from an object based on the provided path.
   * @param obj The object to traverse.
   * @param path An array of keys representing the path to the desired value.
   * @returns The value at the specified path or undefined if not found.
   */
  private getNestedValue(obj: Record<string, any>, path: string[]): any {
    return path.reduce((acc, key) => (acc && acc[key] !== undefined) ? acc[key] : undefined, obj);
  }

  /**
   * Retrieves a configuration value based on the provided dot-separated path.
   * @param path The dot-separated path to the configuration value.
   * @returns The configuration value cast to type T.
   */
  get<T>(path: string): T {
    return this.getNestedValue(this.data, path.split('.')) as T;
  }

  /**
   * Sets a configuration value based on the provided dot-separated path.
   * @param path The dot-separated path to the configuration value.
   * @param value The value to set.
   */
  set<T>(path: string, value: T): void {
    const parts = path.split('.');
    const lastKey = parts.pop()!;
    let current = this.data;
    let currentStructure = this.structure;

    for (const key of parts) {
      if (!(key in current)) {
        current[key] = {};
      }
      current = current[key];
      currentStructure = (currentStructure[key]?.children || {}) as Record<string, ConfigurationStructure>;
    }

    if (lastKey in currentStructure) {
      const validatedValue = this.validateValue(value, currentStructure[lastKey]);
      if (validatedValue === undefined) {
        throw new Error(`Invalid type for ${path}. Expected ${currentStructure[lastKey].type}`);
      }
      current[lastKey] = validatedValue;
    } else {
      current[lastKey] = value;
    }
  }

  /**
   * Serializes the configuration data to a JSON object, including default values.
   * @returns The serialized configuration data.
   */
  toJSON(): Record<string, any> {
    return JSON.parse(JSON.stringify(this.data));
  }

  /**
   * Deserializes a JSON string into a Configuration instance.
   * @param structure The configuration structure defining types and defaults.
   * @param json The JSON string to deserialize.
   * @returns A new Configuration instance.
   */
  static fromJSON(
    structure: Record<string, ConfigurationStructure>,
    json: string
  ): Configuration {
    return new Configuration(structure, JSON.parse(json));
  }

  /**
   * Compare the current values with a JSON object representing possibly alternative values.
   * Return true if the values are identical, false otherwise.
   * @param jsonObject The JSON object to compare.
   * @returns True if the values are identical, false otherwise.
   */
  areSameValues(jsonObject: Record<string, any>): boolean {
    return this.deepEqual(this.data, jsonObject);
  }

  /**
   * Recursively compares two values for deep equality.
   * @param a First value to compare.
   * @param b Second value to compare.
   * @returns True if the values are deeply equal, false otherwise.
   */
  private deepEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      if (!keysB.includes(key)) return false;
      if (!this.deepEqual(a[key], b[key])) return false;
    }

    return true;
  }

}

abstract class BaseConfiguration {
  protected config: Configuration;

  constructor(structure: Record<string, ConfigurationStructure>, configData: Record<string, any> = {}) {
    this.config = new Configuration(structure, configData);
  }

  public getJson(): Record<string, any> {
    return this.config.toJSON();
  }

  public get<T>(path: string): T {
    return this.config.get<T>(path);
  }

  public set<T>(path: string, value: T): void {
    this.config.set(path, value);
  }

  public areSameValues(jsonObject: Record<string, any>): boolean {
    return this.config.areSameValues(jsonObject);
  }

  public static fromJSON(json: string): BaseConfiguration {
    throw new Error('fromJSON must be implemented in derived classes');
  }
}

export class CustomerConfiguration extends BaseConfiguration {
  private static structure: Record<string, ConfigurationStructure> = {
    version_11: {
      type: ConfigurationType.Boolean,
      default: true,
    },
    language: {
      type: ConfigurationType.Object,
      children: {
        preferredLocale: {
          type: ConfigurationType.String,
          default: 'en',
        },
      },
    },
    album: {
      type: ConfigurationType.Object,
      children: {
        show: {
          type: ConfigurationType.Boolean,
          default: true,
        },
        jumpDirectToSlideShow: {
          type: ConfigurationType.Boolean,
          default: true,
        },
        showBigNavigation: {
          type: ConfigurationType.Boolean,
          default: true,
        },
      },
    },
    calendar: {
      type: ConfigurationType.Object,
      children: {
        dayview: {
          type: ConfigurationType.Object,
          children: {
            show: {
              type: ConfigurationType.Boolean,
              default: true,
            },
          },
        },
        monthview: {
          type: ConfigurationType.Object,
          children: {
            show: {
              type: ConfigurationType.Boolean,
              default: true,
            },
          },
        },
        clock: {
          type: ConfigurationType.Object,
          children: {
            show: {
              type: ConfigurationType.Boolean,
              default: true,
            },
          },
        },
      },
    },
    audio: {
      type: ConfigurationType.Object,
      children: {
        show: {
          type: ConfigurationType.Boolean,
          default: false,
        },
      },
    },
    radio: {
      type: ConfigurationType.Object,
      children: {
        show: {
          type: ConfigurationType.Boolean,
          default: true,
        },
      },
    },
    games: {
      type: ConfigurationType.Object,
      children: {
        minisudoku: {
          type: ConfigurationType.Object,
          children: {
            show: {
              type: ConfigurationType.Boolean,
              default: true,
            },
          },
        },
        solitaire: {
          type: ConfigurationType.Object,
          children: {
            show: {
              type: ConfigurationType.Boolean,
              default: true,
            },
          },
        },
        jigsaw: {
          type: ConfigurationType.Object,
          children: {
            show: {
              type: ConfigurationType.Boolean,
              default: false,
            },
            // easy, medium, hard, userChooses -- the first three skips the user choosing screen
            difficultyChoice: {
              type: ConfigurationType.String,
              default: 'userChooses',
            },
            showShowImageButton: {
              type: ConfigurationType.Boolean,
              default: true,
            },
            showOnlyEdgePiecesButton: {
              type: ConfigurationType.Boolean,
              default: true,
            },
          },
        },
      },
    },
    // Add more top-level configuration options here if needed
  };

  constructor(configData: Record<string, any> = {}) {
    super(CustomerConfiguration.structure, configData);
  }

  public static override fromJSON(json: string): CustomerConfiguration {
    return new CustomerConfiguration(JSON.parse(json));
  }

  /**
   * Retrieves the customer configuration from the database.
   * If the configuration is modified (by a new structure), it will be saved to the database.
   * @returns A promise that resolves to a CustomerConfiguration instance.
   * @throws An error if fetching the configuration fails.
   */
  public static async getFromDatabase(): Promise<CustomerConfiguration> {
    const supabaseClientStore = useSupabaseClientStore()
    const supabase = supabaseClientStore.supabase;
    const authStore = useAuthStore();
    const userId = authStore.userId;
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single()

    if (error) {
      throw new Error(`Error fetching customer configuration: ${error.message}`);
    }
    const jsonData = data.userconf;
    const config = new CustomerConfiguration(jsonData);
    if (!config.areSameValues(jsonData)) {
      await config.saveToDatabase();
    }
    return config;
  }

  /**
   * Saves the current configuration to the database.
   * @returns A promise that resolves when the save operation is complete.
   * @throws An error if the save operation fails.
   */
  public async saveToDatabase(): Promise<void> {
    const supabaseClientStore = useSupabaseClientStore();
    const supabase = supabaseClientStore.supabase;
    const authStore = useAuthStore();
    const userId = authStore.userId;

    const { error } = await supabase
      .from('users')
      .update({ userconf: this.getJson() })
      .eq('id', userId);

    if (error) {
      throw new Error(`Error saving customer configuration: ${error.message}`);
    }
  }
}

export class TestConfiguration extends BaseConfiguration {
  private static structure: Record<string, ConfigurationStructure> = {
    album: {
      type: ConfigurationType.Object,
      children: {
        jumpDirectToSlideShow: {
          type: ConfigurationType.Boolean,
          default: true,
        },
        showBigNavigation: {
          type: ConfigurationType.Boolean,
          default: true,
        },
      },
    },
    nested: {
      type: ConfigurationType.Object,
      children: {
        property: {
          type: ConfigurationType.Object,
          children: {
            value: {
              type: ConfigurationType.String,
              default: 'test',
            },
          },
        },
      },
    },
    feature: {
      type: ConfigurationType.Object,
      children: {
        enabled: {
          type: ConfigurationType.Boolean,
          default: true,
        },
      },
    },
    settings: {
      type: ConfigurationType.Object,
      children: {
        maxItems: {
          type: ConfigurationType.Number,
          default: 10,
        },
      },
    },
    feature1: {
      type: ConfigurationType.Object,
      children: {
        enabled: {
          type: ConfigurationType.Boolean,
          default: true,
        },
        settings: {
          type: ConfigurationType.Object,
          children: {
            maxItems: {
              type: ConfigurationType.Number,
              default: 5,
            },
            labels: {
              type: ConfigurationType.Array,
              default: ['one', 'two', 'three'],
            },
          },
        },
      },
    },
    feature2: {
      type: ConfigurationType.Object,
      children: {
        enabled: {
          type: ConfigurationType.Boolean,
          default: false,
        },
        nestedObject: {
          type: ConfigurationType.Object,
          children: {
            key1: {
              type: ConfigurationType.String,
              default: 'value1',
            },
            key2: {
              type: ConfigurationType.String,
              default: 'value2',
            },
          },
        },
      },
    },
    // Add more top-level configuration options here if needed
  };

  constructor(configData: Record<string, any> = {}) {
    super(TestConfiguration.structure, configData);
  }

  public static override fromJSON(json: string): TestConfiguration {
    return new TestConfiguration(JSON.parse(json));
  }
}
